import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChange, AfterViewInit, OnDestroy, Renderer2, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";

import { BaseComponent, UserService, environment, WindowService, EventBusService, WorkspaceService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { take } from "rxjs/operators";
import { MessageInputComponent } from "../message-input/message-input.component";
import { SimpleSignupComponent } from "../modals";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-chat",
  templateUrl: "chat.component.html",
  standalone: false,
})
export class ChatComponent extends BaseComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() participantMap: any;
  @Input() height: number;
  @Input() fullHeight: boolean;
  @Input() forceDark: boolean;
  @Input() mentionDataSource: any;
  @Input() parentThreadMap: any = {};

  private _admin: boolean = false;
  @Input() set admin(value) {
    this._admin = value;
  }
  get admin() {
    return this._admin;
  }

  public messagesSet: boolean = false;

  private _messages: Array<any> = [];
  @Input() set messages(messages) {
    this._messages = messages;
  }

  get messages() {
    return this._messages;
  }
  @Input() enableDirectMessages: boolean = true;
  @Input() commentsUI: boolean = false;
  @Input() sendOnEnter: boolean = false;

  @Output() messageSent: EventEmitter<any> = new EventEmitter();
  @Output() returnPressed: EventEmitter<any> = new EventEmitter();
  @Output() editMessage: EventEmitter<any> = new EventEmitter();
  @Output() deleteMessage: EventEmitter<any> = new EventEmitter();
  @Output() adminDeleteMessage: EventEmitter<any> = new EventEmitter();
  @Output() commentFlagged: EventEmitter<any> = new EventEmitter();
  @Output() commentUnflagged: EventEmitter<any> = new EventEmitter();
  @Output() commentLiked: EventEmitter<any> = new EventEmitter();
  @Output() flagCleared: EventEmitter<any> = new EventEmitter();
  @ViewChild("msgInput") msgInput: MessageInputComponent;
  @ViewChild("chatWindow") chatWindow: ElementRef;
  public newMessage: string;

  public participants: Array<any>;

  // `attingCollabs` / `highlightedAtCollab` lived here unused -- leftovers from an
  // earlier mention implementation. The picker owns that state now.
  public selectedAtCollab: any;
  public selectedAtCollabsInMessage: any = {};

  public isTyping: boolean = false;
  private _typingTimeout: any;
  private _typingAt: boolean;

  public parentMessages: Array<any>;
  public replyingMessageKey: string;
  public showNewMessagesIndicator: boolean = false;
  public previewMessage: any = null;
  public previewMessageVisible: boolean = false;
  private _previewTimeout: any;
  private _isScrolledToBottom: boolean = true;
  private _scrollListener: () => void;

  constructor(
    public userService: UserService,
    private _win: WindowService,
    private _eventbusService: EventBusService,
    public workspaceService: WorkspaceService,
    public appService: AppService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private router: Router
  ) {
    super(userService);
  }

  /**
   * Mentions in chat were rendered as chips but did nothing when clicked -- the
   * delegated handler only ever existed on <myp-comment>. Same behaviour here as
   * in a community thread: open the mentioned person's profile.
   */
  private _removeClickListener: () => void;

  handleMentionClick(event: any) {
    const id = event && event.target && event.target.dataset ? event.target.dataset.mentionid : null;
    if (!id || id === "studio_one_experts") return;
    this.router.navigate(["/profile", id]);
  }

  ngOnInit() {
    this._removeClickListener = this.renderer.listen(this.elementRef.nativeElement, "click", (event) => this.handleMentionClick(event));
    this.scrollChatToBottom();
    this.participants = [];
    for (var i in this.participantMap) {
      this.participants.push(this.participantMap[i]);
    }
  }

  ngAfterViewInit() {
    if (this.chatWindow && this.chatWindow.nativeElement) {
      this._scrollListener = () => this._onScroll();
      this.chatWindow.nativeElement.addEventListener("scroll", this._scrollListener);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this._removeClickListener) this._removeClickListener();
    if (this.chatWindow && this.chatWindow.nativeElement && this._scrollListener) {
      this.chatWindow.nativeElement.removeEventListener("scroll", this._scrollListener);
    }
  }

  private _onScroll() {
    const el = this.chatWindow.nativeElement;
    this._isScrolledToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
    if (this._isScrolledToBottom) {
      this.showNewMessagesIndicator = false;
      if (this.previewMessage) {
        if (this._previewTimeout) clearTimeout(this._previewTimeout);
        this.previewMessage = null;
        this.previewMessageVisible = false;
      }
    }
  }

  public displayedMessagesMap: any = {};

  ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    if (changes.messages) {
      this.scrollChatToBottom();
    }
  }

  onNewMessageReceived(message?: any) {
    if (this._isScrolledToBottom) {
      this.scrollChatToBottom(true);
    } else {
      this._showPreviewMessage(message);
    }
  }

  private _showPreviewMessage(message?: any) {
    if (this._previewTimeout) clearTimeout(this._previewTimeout);
    if (message) {
      this.previewMessage = message;
      this.showNewMessagesIndicator = false;
      this._win.setTimeout(() => {
        this.previewMessageVisible = true;
      }, 20);
      this._previewTimeout = setTimeout(() => {
        this.previewMessageVisible = false;
        this._previewTimeout = setTimeout(() => {
          this.previewMessage = null;
          this.showNewMessagesIndicator = true;
        }, 300);
      }, 3000);
    } else {
      this.showNewMessagesIndicator = true;
    }
  }

  onPreviewClicked() {
    if (this._previewTimeout) clearTimeout(this._previewTimeout);
    this.previewMessage = null;
    this.previewMessageVisible = false;
    this.scrollChatToBottom(true);
  }

  messageValueChange(args) {
    this.newMessage = args;
  }

  sendMessage(args?, parentMsg?, replyingToMessage?) {
    if (this.user) {
      let sentMsg: any = {
        message: args && typeof args === "object" && "value" in args ? args.value : args || this.newMessage,
      };
      if (sentMsg.message) {
        if (parentMsg) {
          sentMsg.parentID = parentMsg.key;
          sentMsg.parentMessage = parentMsg;
        }
        if (replyingToMessage) {
          sentMsg.replyingToMessage = replyingToMessage;
        }
        this.selectedAtCollabsInMessage = {};
        this.messageSent.next(sentMsg);
        this.newMessage = "";
        this.msgInput.emptyInput();
        if (!parentMsg) this.scrollChatToBottom();
      }
    } else {
      this.appService.promptSimpleSignup(SimpleSignupComponent).then(
        () => {
          this.sendMessage(args, parentMsg, replyingToMessage);
        },
        (error) => {}
      );
    }
  }

  sendReply(args) {
    this.replyingMessageKey = args.comment.key;
    this.sendMessage(args.reply, args.comment, args.replyingTo);
  }

  toggleReplyUI(msg) {
    msg.replying = msg.replying ? false : true;
    this.replyingMessageKey = msg.replying ? msg.key : undefined;
  }

  flagComment(msg) {
    if (msg.parentID) this.replyingMessageKey = msg.parentID;
    this.commentFlagged.next(msg);
  }

  unflagComment(msg) {
    if (msg.parentID) this.replyingMessageKey = msg.parentID;
    this.commentUnflagged.next(msg);
  }

  likeComment(msg) {
    if (msg.parentID) this.replyingMessageKey = msg.parentID;
    this.commentLiked.next(msg);
  }

  scrollChatToBottom(smooth?: boolean) {
    this._win.setTimeout(() => {
      if (this.chatWindow && this.chatWindow.nativeElement) {
        if (smooth) {
          this.chatWindow.nativeElement.scrollTo({ top: this.chatWindow.nativeElement.scrollHeight, behavior: "smooth" });
        } else {
          this.chatWindow.nativeElement.scrollTop = this.chatWindow.nativeElement.scrollHeight;
        }
      }
      this._isScrolledToBottom = true;
      this.showNewMessagesIndicator = false;
    }, 25);
  }

  deleteComment(msg) {
    this.replyingMessageKey = msg.parentID;
    this.deleteMessage.next(msg);
  }

  adminDeleteComment(msg) {
    this.replyingMessageKey = msg.parentID;
    this.adminDeleteMessage.next(msg);
  }

  editComment(msg) {
    this.replyingMessageKey = msg.parentID;
    this.editMessage.next(msg);
  }

  clearFlag(msg) {
    this.flagCleared.next(msg);
  }

  msgOptions(msg) {
    let args: any = {
      title: msg.message,
      actions: ["Delete Message"],
    };
    if (msg.user_id == this.userService.user.id) args.actions.unshift("Edit Message");
    this._eventbusService.emit(this._eventbusService.types.showActionChooser, args);
    this._eventbusService
      .observe(this._eventbusService.types.actionChosen)
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          if (result == "Edit Message") {
            this.replyingMessageKey = msg.parentID;
            this.editMessage.next(msg);
          } else if (result == "Delete Message") {
            this.replyingMessageKey = msg.parentID;
            this.deleteMessage.next(msg);
          }
        }
      });
  }
}
